/**
 * The bundle's status (decision 7; part 24a, table K): under the toolbar while a bundle is made or has something to say,
 * never over the text, as the PDF export's is. Its progress and Cancel; then the zip to save, with what it leaves out,
 * or what went wrong with Try again.
 */
import { BUNDLE_ARCHIVE } from '@cockatrice/core';
import type { BundleState } from '../bundle/controller.ts';
import { focusOn } from './announce.ts';
import { el, when } from '../ui/dom.ts';
import type { Child } from '../ui/dom.ts';
import { JobProgress, JobStatus } from './job-status.ts';
import type { JobCount } from './job-status.ts';
import type { Shell } from './shell.ts';
import { component } from '../ui/solid.ts';
import Button from '../ui/Button.ts';

/** What the status says (K-rows' outcomes). */
export function bundleText(shell: Shell, state: BundleState): string {
  const { t } = shell;
  switch (state.kind) {
    case 'idle':
      return '';
    case 'saving':
      return t('bundle.saving');
    case 'exporting':
      return state.done >= state.total && state.total > 0 ? t('bundle.finishing') : t('bundle.progress', { percent: state.total === 0 ? 0 : Math.floor((100 * state.done) / state.total) });
    case 'ready':
      // Part 11c, deep-fix r6: "every chapter" only when nothing is left out or kept as stored (K1).
      return state.unreadable > 0 || state.unsaved || state.tooMany ? t('bundle.readyExceptions') : t('bundle.ready');
    case 'failed':
      return state.reason === 'outdated' ? t('bundle.outdated') : state.reason === 'too-large' ? t('bundle.tooLarge') : t('bundle.failed');
  }
}

/** K1: what the writer should know of a finished bundle: records that couldn't be read (in it as stored), typing not saved. */
function exceptions(shell: Shell, state: BundleState): string[] {
  if (state.kind !== 'ready') return [];
  const { t } = shell;
  return [
    ...(state.unreadable > 0 ? [t('bundle.unreadable', { count: state.unreadable })] : []),
    ...(state.unsaved ? [t('bundle.unsaved')] : []),
    ...(state.tooMany ? [t('bundle.tooManyFiles', { count: BUNDLE_ARCHIVE.files })] : []),
  ];
}

/** K1: the exceptions on screen, one line each. */
function leftOut(shell: Shell, state: BundleState): Child {
  return exceptions(shell, state).map((line) => el('p', { class: 'bundle-left-out' }, line));
}

/**
 * Part 11c, A13: what the bundle announces: its phases, never each percent the status shows; a finished bundle with
 * exceptions says each of them with it (deep-fix r6), so a screen reader never hears "every chapter" of one that isn't.
 */
export function bundleNews(shell: Shell, state: BundleState): string {
  if (state.kind === 'exporting' && !(state.done >= state.total && state.total > 0)) return shell.t('bundle.making');
  return [bundleText(shell, state), ...exceptions(shell, state)].join(' ');
}

/**
 * The status region, shown while the bundle isn't idle; what it announces is {@link bundleNews}, said by the
 * workspace's announcers (A13); its buttons leave the focus as the PDF export's do (A24).
 */
export function BundleStatus(shell: Shell): Child {
  const { t, bundler } = shell;
  const state = (): BundleState => bundler.state();
  const running = (): boolean => state().kind === 'saving' || state().kind === 'exporting';
  const count = (): JobCount => {
    const now = state();
    return now.kind === 'exporting' ? now : null;
  };
  return [
    when(
      () => state().kind !== 'idle',
      () =>
        JobStatus({
          class: 'bundle-status',
          label: t('bundle.label'),
          tone: () => (state().kind === 'failed' ? 'danger' : state().kind === 'ready' ? (exceptions(shell, state()).length === 0 ? 'success' : 'warn') : 'neutral'),
          children: [
            el('p', {}, () => bundleText(shell, state())),
            () => leftOut(shell, state()),
            JobProgress(t('bundle.label'), count),
          ],
          actions: [
            when(running, () =>
              component(Button, {
                size: 'sm',
                class: 'cancel-bundle',
                onClick: () => {
                  bundler.cancel();
                  focusOn(shell, '.export-bundle');
                },
                children: t('export.cancel'),
              }),
            ),
            () => {
              const now = state();
              // Part 24e, T8: a bundle saved by hand counts as the book's backup.
              return now.kind === 'ready' ? component(Button, { size: 'sm', class: 'save-bundle', href: now.url, download: now.name, onClick: () => void shell.backups.exported(now.bookId, now.sha256, now.at), children: t('bundle.save') }) : null;
            },
            when(
              () => state().kind === 'failed',
              () =>
                component(Button, {
                  size: 'sm',
                  onClick: () => {
                    bundler.start();
                    focusOn(shell, '.cancel-bundle');
                  },
                  children: t('export.retry'),
                }),
            ),
            when(
              () => !running(),
              () =>
                component(Button, {
                  variant: 'ghost',
                  size: 'sm',
                  onClick: () => {
                    bundler.dismiss();
                    focusOn(shell, '.export-bundle');
                  },
                  children: t('export.dismiss'),
                }),
            ),
          ],
        }),
    ),
  ];
}
